/*----------------------------------
- WEBSITE: ASK AI ROW
----------------------------------*/

/**
 * "Ask AI about Unique Domains" on the marketing `AskAi` part, for every rebuilt page: one pill per assistant of the
 * repo's one provider catalog, each opening the page's prompt in that assistant. The page passes its prompt context
 * and its copy; the marks, the hrefs and the click event are the same everywhere. Each pill leads with the assistant's
 * own colour logo from the provider catalog (Gaetan's decision 4A, plan decision 24), on the kit `Mark`'s logo tile;
 * the glyph seed stays its letter fallback. It replaces the shipped
 * `HomeAskAiSection` page by page (the pages not yet rebuilt keep it).
 *
 * @docs docs/features/website-marketing-layer
 * @adr  ADR-0110
 * @rule The click keeps the shipped event and placement (`ask_ai_menu_opened`, `home_footer_ask_ai`) on every page, so
 *       the series does not split when a page moves; the page is told apart by the prompt's page url.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useMemo } from 'preact/hooks';

import { buildAskYourAIPrompt, type AskYourAIProps } from '@uniquedomains/components/ask-ai';
import { ASK_AI_PROVIDERS, buildAskAiProviderHref, resolveAskAiIconSrc } from '@uniquedomains/components/ask-ai/providers';
import { Mark } from '@uniquedomains/components/kit/mark/Mark';
import { AskAi, type AskAiPill } from '@uniquedomains/components/marketing/ask-ai/AskAi';
import { ASK_AI_PROVIDER_SEEDS } from '@uniquedomains/components/marketing/ask-ai/askAiSeeds';

import { track } from '@/client/pages/landing/lib/analytics';

/*----------------------------------
- TYPES
----------------------------------*/

type WebsiteAskAiProps = {
    title: string;
    /** The page's prompt context (page type, intent, url, title, GEO context). */
    context: AskYourAIProps;
    /**
     * Each pill's tooltip, from the page's catalog ("Ask ChatGPT about Unique Domains"). The pill's accessible name
     * stays its visible text: an `aria-label` that does not hold the visible text (the mark's name included)
     * fails WCAG 2.5.3, so a voice user saying "ChatGPT" would miss it (Lighthouse `label-content-name-mismatch`,
     * batch 7 review M6). The row's line above already says what the pills ask about.
     */
    providerTitle: (provider: string) => string;
    class?: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const ASK_AI_PLACEMENT = 'home_footer_ask_ai';

/*----------------------------------
- COMPONENT
----------------------------------*/

const WebsiteAskAi = ({ title, context, providerTitle, class: klass }: WebsiteAskAiProps) => {
    const pills = useMemo<AskAiPill[]>(() => {
        const prompt = buildAskYourAIPrompt(context);
        return ASK_AI_PROVIDERS.map((provider) => ({
            id: provider.id,
            label: provider.label,
            href: buildAskAiProviderHref(provider, prompt),
            // The logo sits beside the visible name, so it stays decorative (an empty alt), as the letter glyph did.
            glyph: <Mark letter={provider.label} color={ASK_AI_PROVIDER_SEEDS[provider.id]} size="lg" logo={resolveAskAiIconSrc(provider.icon)} />,
            title: providerTitle(provider.label),
            onClick: () => track('ask_ai_menu_opened', { provider: provider.id, placement: ASK_AI_PLACEMENT }),
        }));
    }, [context, providerTitle]);

    return <AskAi class={klass} title={title} pills={pills} />;
};

/*----------------------------------
- EXPORTS
----------------------------------*/

export default WebsiteAskAi;
